import { useSyncExternalStore } from "react";

/** Transient notifications shown top-center: errors stay longer, and an identical visible message is not repeated. */
export type ToastTone = "info" | "success" | "warn" | "error";
export interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
  detail?: string;
  duration: number;
}

const DURATION: Record<ToastTone, number> = { info: 5000, success: 5000, warn: 8000, error: 12000 };
const MAX_VISIBLE = 4;

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function toast(tone: ToastTone, text: string, detail?: string): number {
  const same = toasts.find((t) => t.tone === tone && t.text === text && t.detail === detail);
  if (same) dismissToast(same.id);
  const item: Toast = { id: nextId++, tone, text, detail, duration: DURATION[tone] };
  toasts = [...toasts, item].slice(-MAX_VISIBLE);
  emit();
  return item.id;
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => toasts,
    () => toasts,
  );
}
